import { EnterOutlined, PullRequestOutlined } from "@ant-design/icons";
import React, { ReactNode, useMemo } from 'react';
import Icon, { ICON_NAME } from "../../../../../../components/Icon";
import Menu from "../../../../../../components/Menu";
import Density from "../../../../../../components/Operation/Density2";
import { datetime } from "../../../../../../util/library/datetime";
import { SHORT_DATETIME_FORMAT } from "../../../../../../util/varibles/constants";
import { cloneObj, convertStepName, durationTxt, formatNumber } from "../../../../../../util/varibles/global";
import { IRoundPlan } from "../../../../../../util/varibles/interface";
import { useRoundMap } from "../../EditOperationContext/RoundMapContext";
import styles from "./style.module.scss";

const Info: React.FC<{
    data: any
    name: string
    total_weight?: number
    max_density?: number
    menu: { key: string, label: ReactNode }[]
}> = ({ data, name, total_weight, max_density, menu }) => {
    const { keys = [], density } = data || {};
    return <Menu trigger={['contextMenu']} items={menu} placement={"bottom"}>
        <div className={styles.tank}>
            <div className={['h-[52px]', styles.tankInfo].join(' ')} data-lev='job'>
                <span>{total_weight ? <>{formatNumber(Math.round(total_weight / 1000) / 1000)}t</> : '--'}</span>
                <span>
                    {keys.length > 0 ? <>{convertStepName(keys).join(', ')}: {durationTxt(data.duration)}</> : '--'}
                </span>
            </div>
            <div className={['h-[24px]', styles.tankInfo].join(' ')}>
                <span className="whitespace-nowrap text-[14px]">{name}</span>
                {density !== undefined ?
                    <Density density={density} max_density={max_density} /> :
                    <div>--</div>}
            </div>
        </div>
    </Menu>;
};

const Job: React.FC<{ data: { keys: string[], [key: string]: any } }> = ({ data }) => {
    const { start_time, finish_time, keys = [], waiting_equipment } = data;
    const names = Array.from(keys);
    const { format = SHORT_DATETIME_FORMAT } = useRoundMap()

    return <div className='relative'>
        <div className={styles.time}>
            {waiting_equipment && <span className='text-warn text-right pr-3 pt-[5px] mr-1 whitespace-nowrap'>
                ({durationTxt(waiting_equipment)}) Waiting for equipment
            </span>}
            <span>{start_time ? datetime(start_time).format(format) : "--"}</span>
        </div>
        <JobName
            className='pr-3'
            names={convertStepName(names).map(key => key.replace('unit', data.destination_name))}
            bold={400}
        />
        <div className={[styles.arrow, 'arrow-down'].join(' ')} />
        <div className={styles.time}>{finish_time ? datetime(finish_time).format(format) : "--"}</div>
    </div>;
};

const JobName: React.FC<{ names: string[], className?: string, bold?: number }> = ({
    names = [],
    className,
    bold = 600
}) => {
    return <div className={`flex flex-col items-end py-[5px] ${className || ''} min-h-[32px] font-[${bold}] text-[14px]`}>
        {names.join(" - ")}
    </div>;
};

interface IProps {
    data: IRoundPlan['tanks'][0]
    nextUnit?: boolean
    canSeparate?: boolean

    onParallel?(): void

    onSeparate?(): void

    onChangeTank?(isAdd: boolean): void
}

const Tank: React.FC<IProps> = ({
    data,
    nextUnit,
    canSeparate,
    onParallel = () => null,
    onSeparate = () => null,
    onChangeTank = () => null
}) => {
    const { start_places = [], finish_places = [], processes = [], tank, total_weight, density, max_density } = data;
    const { isEdit } = useRoundMap();

    const { loading, tankData, tasks } = useMemo(() => {
        const jobs: IRoundPlan['tanks'][0]['processes'] = cloneObj(processes);
        const first = (jobs || []).shift() || { keys: [] };
        const result: any = {};
        if ((first.keys || []).some((key: string) => key === 'loading')) {
            result.loading = <Job data={first} />;
            result.tasks = jobs;
        } else {
            result.loading = <div className='h-[76px]' />;
            result.tasks = cloneObj(processes);
        }
        const tankIndex = result.tasks.findIndex((job: any) => job.type === 'tank');
        const tankData = tankIndex > -1 ? result.tasks.splice(tankIndex, 1)[0] : {}

        return { ...result, tankData }
    }, [processes]);

    const menu = useMemo(() => {
        if (!isEdit)
            return []
        if (total_weight)
            return [
                ...canSeparate ? [{
                    key: 'separate',
                    onClick: onSeparate,
                    label: <div className='menu-line'>
                        <EnterOutlined />
                        <span>Move unit to a new round</span>
                    </div>
                }] : [],
                {
                    key: 'add',
                    onClick: () => onChangeTank(true),
                    label: <div className='menu-line'>
                        <Icon icon={ICON_NAME.ADD} />
                        <span>Add tank</span>
                    </div>
                }, {
                    key: 'delete',
                    onClick: () => onChangeTank(false),
                    label: <div className='menu-line' data-danger='true'>
                        <Icon icon={ICON_NAME.DELETE} danger />
                        <span>Delete tank</span>
                    </div>
                }
            ]
        else if (nextUnit)
            return [{
                key: 'merge',
                onClick: onParallel,
                label: <div className='menu-line'>
                    <PullRequestOutlined />
                    <span>Allow processing of next unit</span>
                </div>
            }]
        else return []
    }, [isEdit, total_weight, nextUnit, canSeparate, onParallel, onSeparate, onChangeTank]);

    return <div>
        <JobName names={start_places} />
        {loading}
        <Info
            menu={menu}
            data={{ ...tankData, density: tankData?.density || density }}
            name={tank}
            total_weight={total_weight}
            max_density={max_density}
        />
        {tasks.map((item: any, i: number) => <Job key={i} data={item} />)}
        <JobName names={finish_places} />
    </div>;
}

export default Tank;
